import { Card } from "@/components/Card";
import { useTracking } from "@/requests/hooks/app";
import { faCaretDown, faCaretUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { tv } from "tailwind-variants";

const accordion = tv({
  slots: {
    base: "group w-full p-5",
    checkbox: "hidden",
    header:
      "flex w-full grow cursor-pointer flex-row items-center justify-between gap-10 rounded-sm bg-gray-light p-20 md:gap-20",
    toggleContainer:
      "toggle-button flex h-30 w-30 items-center justify-center rounded-full border-none bg-primary bg-gradient-to-r group-has-checked-within:from-primary group-has-checked-within:to-green",
    toggle:
      "flex h-[28px] w-[28px] items-center justify-center rounded-full bg-gray-light text-center peer-checked:bg-red",
    childrenContent:
      "h-0 overflow-hidden transition-[height] duration-200 ease-in-out peer-checked:h-0 peer-checked:overflow-scroll",
    childPadding: "p-20",
  },
  variants: {
    variant: {
      disableAnimation: {
        childrenContent: "transition-none",
      },
    },
    disableChildPadding: {
      true: {
        childPadding: "p-0",
      },
    },
  },
});
interface IProps {
  defaultIsExpanded?: boolean;
  disableExpandable?: boolean;
  disableChildPadding?: boolean;
  hideToggle?: boolean;
  topContent: string | JSX.Element;
  children: string | JSX.Element | JSX.Element[];
  trackingId?: IGetEventProperties | string;
  hasShadow?: boolean;
}

const Accordion = ({
  defaultIsExpanded = false,
  disableChildPadding = false,
  children,
  topContent,
  disableExpandable,
  hideToggle,
  trackingId,
  hasShadow = true,
}: IProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultIsExpanded);
  const [disableInitialAnimation, setDisableInitialAnimation] =
    useState<boolean>(defaultIsExpanded);
  const [contentHeight, setContentHeight] = useState<string>("0");
  const childrenRef = useRef<HTMLDivElement>(null);
  const { mutate: sendTracking } = useTracking();

  const {
    base,
    header,
    toggleContainer,
    toggle,
    childrenContent,
    checkbox,
    childPadding,
  } = accordion({
    variant: disableInitialAnimation ? "disableAnimation" : undefined,
    disableChildPadding,
  });

  useEffect(() => {
    const checkChildrenChildrenHeight = () => {
      if (childrenRef?.current) {
        const { clientHeight } = childrenRef.current;
        const setHeight = `${clientHeight}px`;
        setContentHeight(setHeight);
      }
    };
    const resizeObserver = new ResizeObserver(checkChildrenChildrenHeight);
    resizeObserver.observe(childrenRef?.current!);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (
      disableInitialAnimation &&
      childrenRef.current?.clientHeight === parseInt(contentHeight)
    )
      setDisableInitialAnimation(false);
  }, [contentHeight, disableInitialAnimation]);

  const toggleAccordion = () => {
    if (disableExpandable || hideToggle) return;

    setIsExpanded(expanded => !expanded);

    if (typeof trackingId === "string") {
      sendTracking({
        eventName: trackingId,
        additionalProperties: {
          action: isExpanded ? "close" : "open",
        },
      });
    } else if (typeof trackingId === "object") {
      sendTracking(trackingId);
    }
  };

  const onChange = () => {};

  const renderToggle = () => {
    if (hideToggle) return null;

    return (
      <div className={toggleContainer()}>
        <div className={toggle()}>
          <FontAwesomeIcon icon={isExpanded ? faCaretUp : faCaretDown} />
        </div>
      </div>
    );
  };

  return (
    <Card rounded="sm" backgroundColor="white" shadow={hasShadow}>
      <div className={base()} data-testid="accordion">
        <input
          id="expandCollapse"
          checked={isExpanded}
          type="checkbox"
          className={checkbox()}
          onChange={onChange}
        />
        <label
          htmlFor="expandCollapse"
          className={header()}
          onClick={toggleAccordion}>
          {topContent}
          {renderToggle()}
        </label>
        <div
          className={childrenContent()}
          style={{ height: isExpanded ? contentHeight : "0" }}>
          <div ref={childrenRef}>
            <div className={childPadding()}>{children}</div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default Accordion;
